import MobileTextField from './components/MobileTextField';
import MobileAuthLayout from './components/MobileAuthLayout';
import MobileSsoErrorScreen from './components/MobileSsoErrorScreen';
import SnackbarCloseButton from 'app/shared-components/util/SnackbarCloseButton';
import MobileLoadingOverlay from 'app/pages/mobile/components/MobileLoadingOverlay';
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { CognitoError, selectAuth } from 'app/store/authSlice';
import { styled } from '@mui/material/styles';
import { useAppSelector } from 'app/store';
import { useLoginFlow } from 'app/hooks/useLoginFlow';
import { isEligibleForPasskeyPrompt } from 'app/auth/passkeyPromptSession';
import {
  classifySsoError,
  extractSsoIdentity,
  getPendingSsoAttempt,
  resolveSsoAttempt,
} from 'app/auth/ssoAttemptLog';
import { consumeSsoFailure, subscribeSsoFailure } from 'app/auth/ssoErrorSignal';
import { REPLAY_CHROME_CLASS, sentryMask } from 'sentryReplayPrivacy';
import {
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link,
  InputAdornment,
  Icon,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 30,
  fontWeight: 700,
  lineHeight: '40px',
  marginBottom: theme.spacing(4),
}));

const LoginButton = styled(Button)<{ $isValid?: boolean }>(({ theme, $isValid = true }) => ({
  backgroundColor: $isValid ? theme.palette.primary.main : theme.palette.action.disabledBackground,
  color: $isValid ? theme.palette.primary.contrastText : theme.palette.text.disabled,
  borderRadius: 8,
  padding: theme.spacing(1.5, 4),
  '&:hover': {
    backgroundColor: $isValid ? theme.palette.primary.dark : theme.palette.action.disabledBackground,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
}));

// Pressing Return/Done on the mobile keyboard should just close the keyboard
// (not submit) so the user can see the Sign In button, which floats up above
// the keyboard (MobileAuthLayout binds its height to the visual viewport).
const dismissKeyboardOnEnter = (e: React.KeyboardEvent<HTMLDivElement>): void => {
  if (e.key === 'Enter') {
    e.preventDefault();
    (e.target as HTMLElement).blur();
  }
};

interface SsoFailureView {
  isNotProvisioned: boolean;
  typedEmail?: string;
  returnedIdentity?: string;
}

function MobileSignIn(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { username: rememberedUsername } = useAppSelector(selectAuth);
  const infoSnackbarKey = useRef<number | string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [authError, setAuthError] = useState<any>(null);

  const {
    step,
    email: loginEmail,
    isCheckingSSO,
    isResolvingUsername,
    ssoError,
    handleEmailSubmit,
    handlePasswordSubmit,
    resetToEmailStep,
    startAccountSwitch,
  } = useLoginFlow();

  // Local form state
  const [emailValue, setEmailValue] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // SEE-1200: when an SSO attempt fails, show the error screen instead of
  // silently landing back on the form (the invisible retry loop). The native
  // shell forwards the failure into the WebView as /login query params
  // (SEE-1161): Cognito's error_description, or the shell's own sso_error /
  // sso_cancelled codes.
  const [ssoFailure, setSsoFailure] = useState<SsoFailureView | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // One handler for a Cognito error_description, whichever way it arrives —
  // forwarded as URL params by the native shell (SEE-1161), or on the Amplify
  // Hub when the OAuth flow ran inside the WebView itself.
  const handleSsoErrorDescription = (errorDescription: string): void => {
    // SEE-1251: capture the attempt context BEFORE resolving clears it — the
    // typed email and IdP type feed the recovery UI on the error screen.
    const pending = getPendingSsoAttempt();
    const failureCode = classifySsoError(errorDescription);
    resolveSsoAttempt('failure', { failureCode, reason: errorDescription });

    // Invited but not registered yet — mirror web Login: route to signup.
    if (failureCode === 'registration_required') {
      // Cognito wraps Lambda errors and appends a period ("PreSignUp failed
      // with error REGISTRATION_REQUIRED: a@b.com.") — strip it, or the email
      // fails validation in Register's disabled field and bricks the form.
      const ssoEmail = errorDescription.split('REGISTRATION_REQUIRED:')[1]?.trim().replace(/\.+$/, '');
      if (ssoEmail) {
        enqueueSnackbar('Please complete your account registration before signing in with SSO.', {
          variant: 'info',
          persist: true,
          action: (key) => <SnackbarCloseButton snackbarKey={key} />,
        });
        // REGISTRATION_REQUIRED can only come out of an SSO sign-in, so tell
        // Register directly (?sso=1) instead of making it re-derive this with
        // a network call — that check signs with IAM credentials whose state
        // is unreliable right after a failed SSO redirect (stale session or
        // freshly cleared), and a silent miss shows the password form.
        navigate(`/register/${encodeURIComponent(ssoEmail)}?sso=1`);
        return;
      }
    }

    // Missing profile info in the SSO provider — same actionable message as
    // web Login: the user's IT admin has to fix it, not us.
    if (failureCode === 'missing_profile_attributes') {
      enqueueSnackbar(
        'Your company SSO account is missing required information (first name or last name). Please contact your IT administrator to update your profile.',
        {
          variant: 'error',
          persist: true,
          action: (key) => <SnackbarCloseButton snackbarKey={key} />,
        }
      );
      return;
    }

    // SAML configuration issue — not the user's fault, they need to report it
    if (failureCode === 'invalid_saml') {
      enqueueSnackbar(
        'There was a problem with your company sign-in configuration. Please contact support.',
        {
          variant: 'error',
          persist: true,
          action: (key) => <SnackbarCloseButton snackbarKey={key} />,
        }
      );
      return;
    }

    const typedEmail = (pending?.email || '').toLowerCase().trim();
    const returnedIdentity = extractSsoIdentity(errorDescription);
    setSsoFailure({
      isNotProvisioned: failureCode === 'not_provisioned',
      typedEmail,
      returnedIdentity,
    });
  };

  useEffect(() => {
    const errorDescription = searchParams.get('error_description');
    const nativeErrorCode = searchParams.get('sso_error');
    const cancelled = searchParams.get('sso_cancelled');

    if (errorDescription || nativeErrorCode || cancelled) {
      setSearchParams({}, { replace: true });
    }

    if (errorDescription) {
      handleSsoErrorDescription(errorDescription);
      return;
    }

    if (nativeErrorCode) {
      resolveSsoAttempt('failure', { failureCode: 'native_sso_error', reason: nativeErrorCode });
      setSsoFailure({ isNotProvisioned: false });
      return;
    }

    if (cancelled) {
      // The user dismissed the sign-in sheet on purpose: stay on the form
      // silently, but still terminate the attempt log.
      resolveSsoAttempt('failure', { failureCode: 'cancelled' });
      return;
    }

    if (getPendingSsoAttempt()) {
      // Back on login with an attempt pending and no outcome params — an
      // older shell that can't distinguish cancel from failure, or a lost
      // redirect. Log the terminal event; don't alarm the user.
      resolveSsoAttempt('failure', { failureCode: 'returned_without_session' });
    }
    // Mount-only: the params are consumed (and cleared) on the boot that
    // carries them; re-running on ordinary state changes would misfire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback for SSO that ran inside the WebView itself (no native intercept):
  // Amplify consumes the OAuth callback and reports the failure on the Hub.
  useEffect(() => {
    const unsubscribe = subscribeSsoFailure(handleSsoErrorDescription);
    const latched = consumeSsoFailure();
    if (latched) {
      handleSsoErrorDescription(latched);
    }
    return unsubscribe;
    // Mount-only by design; the handler closes over stable snackbar/nav.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (rememberedUsername) {
      setEmailValue(rememberedUsername);
    }
  }, [rememberedUsername]);

  useEffect(() => {
    if (authError?.name === CognitoError.PasswordReset) {
      enqueueSnackbar(t('passwordMustBeChanged'));
      navigate(`/forgot-password/${loginEmail}`);
    }
  }, [authError, enqueueSnackbar, navigate, t, loginEmail]);

  useEffect(() => {
    if (!authError || authError.name === CognitoError.PasswordReset) {
      return;
    }

    if (authError.name === CognitoError.UserNotConfirmed) {
      infoSnackbarKey.current = enqueueSnackbar(t('accountNotVerified'), {
        variant: 'info',
        autoHideDuration: 30000,
      });
    }

    const isPasswordError = authError.name === CognitoError.NotAuthorized;

    let message: string;
    if (authError.name === CognitoError.UserNotConfirmed) {
      message = t('userNotConfirmedError');
    } else if (authError.name === CognitoError.LambdaError) {
      message = t('accountSetupIncomplete');
    } else {
      message = authError.message;
    }

    if (isPasswordError) {
      setPasswordError(message);
    } else {
      setEmailError(message);
    }
  }, [authError, enqueueSnackbar, t]);

  useEffect(
    () => () => {
      if (infoSnackbarKey.current) {
        closeSnackbar(infoSnackbarKey.current);
      }
    },
    [closeSnackbar]
  );

  const onEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    if (!emailValue.trim()) {
      setEmailError(t('enterEmail'));
      return;
    }
    await handleEmailSubmit(emailValue, remember);
  };

  const onPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (infoSnackbarKey.current) {
      closeSnackbar(infoSnackbarKey.current);
      infoSnackbarKey.current = null;
    }

    setPasswordError('');
    if (!password) {
      setPasswordError(t('enterPassword'));
      return;
    }
    if (password.length < 8) {
      setPasswordError(t('passwordTooShort'));
      return;
    }

    setIsPending(true);
    const result = await handlePasswordSubmit(password, remember);
    setIsPending(false);

    if (result.error) {
      setAuthError(result.error);
    } else {
      if (result.usedDeprecatedUsername) {
        enqueueSnackbar(
          'Success! Just a heads up: We are retiring usernames soon. Please use your email to log in next time.',
          {
            variant: 'info',
            persist: true,
            action: (key) => <SnackbarCloseButton snackbarKey={key} />,
          }
        );
      }
      const locationState = location.state as { from?: { pathname: string } };
      const redirectTo = locationState?.from?.pathname || '/mobile-demo';
      // SEE-214: route eligible users to the passkey prompt right after login
      // (mirrors the web Login flow). The prompt renders on auth alone; the
      // deep-link they were headed to (if any) is forwarded so it continues
      // there afterwards. MobileLanding covers reload/deep-link entries.
      if (isEligibleForPasskeyPrompt(result.user)) {
        navigate('/setup-passkey', {
          replace: true,
          // Always forward the post-login destination so PasskeyPrompt continues
          // straight there, avoiding an extra /landing → /mobile-demo hop.
          state: { from: redirectTo },
        });
      } else {
        navigate(redirectTo);
      }
    }
  };

  const onBackToEmail = () => {
    resetToEmailStep();
    setPassword('');
    setPasswordError('');
    setAuthError(null);
  };

  if (ssoFailure) {
    return (
      <MobileSsoErrorScreen
        isNotProvisioned={ssoFailure.isNotProvisioned}
        typedEmail={ssoFailure.typedEmail}
        returnedIdentity={ssoFailure.returnedIdentity}
        onBackToLogin={() => {
          setSsoFailure(null);
          onBackToEmail();
        }}
        onUseDifferentAccount={
          ssoFailure.typedEmail
            ? () => {
                const typed = ssoFailure.typedEmail as string;
                const returned = ssoFailure.returnedIdentity;
                setSsoFailure(null);
                startAccountSwitch(typed, returned);
              }
            : undefined
        }
      />
    );
  }

  if (isPending || step === 'sso-redirect') {
    return <MobileLoadingOverlay isLoading={true} />;
  }

  const isEmailStep = step === 'email';
  const isPasswordStep = step === 'password';

  return (
    <MobileAuthLayout
      footer={
        <Box
          component="form"
          onSubmit={isEmailStep ? onEmailSubmit : onPasswordSubmit}
          sx={{ width: '100%' }}
        >
          <LoginButton
            type="submit"
            fullWidth
            variant="contained"
            disabled={
              isEmailStep
                ? !emailValue.trim() || isCheckingSSO
                : !password || isPending || isResolvingUsername
            }
            $isValid={
              isEmailStep
                ? !!emailValue.trim() && !isCheckingSSO
                : !!password && !isPending && !isResolvingUsername
            }
            startIcon={
              isCheckingSSO || isResolvingUsername ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {isEmailStep
              ? isCheckingSSO
                ? t('checking') || 'Checking...'
                : t('continue') || 'Continue'
              : isPending || isResolvingUsername
              ? t('loggingIn')
              : t('mobileSignInButton')}
          </LoginButton>
        </Box>
      }
    >
      {/* Replay mask: the disabled email field is type="text" with no autocomplete. */}
      <Box
        {...sentryMask}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Title variant="h4">{t('mobileSignInTitle')}</Title>

        {isEmailStep && (
          <Box>
            <Typography variant="body1" className={REPLAY_CHROME_CLASS} sx={{ mb: 1, fontWeight: 500 }}>
              {t('email')}
            </Typography>
            <MobileTextField
              fullWidth
              variant="outlined"
              type="text"
              autoComplete="email webauthn"
              placeholder={t('mobileSignInEnterEmail') || 'Enter your email'}
              value={emailValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmailValue(e.target.value);
                setEmailError('');
              }}
              error={!!emailError || !!ssoError}
              helperText={emailError || ssoError}
              inputProps={{ enterKeyHint: 'done' }}
              onKeyDown={dismissKeyboardOnEnter}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon className="text-20" color="action">
                      email
                    </Icon>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 400 }}>
                    {t('mobileSignInRememberMe')}
                  </Typography>
                }
              />
            </Box>
          </Box>
        )}

        {isPasswordStep && (
          <>
            <Box>
              <Typography variant="body1" className={REPLAY_CHROME_CLASS} sx={{ mb: 1, fontWeight: 500 }}>
                {t('email')}
              </Typography>
              <MobileTextField
                fullWidth
                variant="outlined"
                type="text"
                value={emailValue}
                disabled
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.5)',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={onBackToEmail} size="small" aria-label="Change email">
                        <Icon className="text-20" color="action">
                          edit
                        </Icon>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box>
              <Typography variant="body1" className={REPLAY_CHROME_CLASS} sx={{ mb: 1, fontWeight: 500 }}>
                {t('password')}
              </Typography>
              <MobileTextField
                fullWidth
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder={t('mobileSignInEnterPassword')}
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value);
                  setPasswordError('');
                }}
                error={!!passwordError}
                helperText={passwordError}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                inputProps={{ enterKeyHint: 'done' }}
                onKeyDown={dismissKeyboardOnEnter}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('togglePasswordVisibility')}
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="large"
                      >
                        <Icon className="text-20" color="action">
                          {showPassword ? 'visibility' : 'visibility_off'}
                        </Icon>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Link
                component="button"
                type="button"
                className={REPLAY_CHROME_CLASS}
                onClick={() => navigate('/forgot-password')}
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'none' },
                }}
              >
                {t('mobileSignInForgotPassword')}
              </Link>
            </Box>
          </>
        )}
      </Box>
    </MobileAuthLayout>
  );
}

export default MobileSignIn;
